<template>
  <b-modal
    id="physicalCountPreviewModal"
    ref="physicalCountPreviewModal"
    size="xl"
    title="สรุปการตรวจนับสต๊อกย้อนหลัง"
    no-close-on-backdrop
    @hidden="onHidden"
  >
    <div
      v-if="items.length > 0"
      class="preview-layout"
    >
      <!-- Left: metadata -->
      <div class="preview-info">
        <div
          v-if="adjustmentNo"
          class="info-row"
        >
          <span class="info-label">รหัส:</span>
          <span class="info-value">{{ adjustmentNo }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">ประเภทการจัดการ:</span>
          <span class="info-value">ตรวจนับสต๊อก</span>
        </div>
        <div class="info-row">
          <span class="info-label">เหตุผล:</span>
          <span class="info-value">{{ reason || "-" }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">ชื่อผู้ทำรายการ:</span>
          <span class="info-value">{{ createdByName || "-" }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">วันที่มีผล:</span>
          <span class="info-value">{{ formatDate(transactionDate) }}</span>
        </div>
        <div
          v-if="countedDt"
          class="info-row"
        >
          <span class="info-label">วันที่นับจริง:</span>
          <span class="info-value">{{ formatDate(countedDt) }}</span>
        </div>
        <div
          v-if="createdDate"
          class="info-row"
        >
          <span class="info-label">วันที่สร้าง:</span>
          <span class="info-value">{{ formatDate(createdDate) }}</span>
        </div>
      </div>

      <!-- Right: product table -->
      <div class="preview-table">
        <b-table
          id="physicalCountPreviewTable"
          hover
          small
          responsive
          sticky-header="min(1048px, calc(100vh - 280px))"
          head-variant="light"
          :items="items"
          :fields="fields"
        >
          <template #head(totalQty)>
            <div>
              จำนวนสต๊อก
              <br />
              {{ formatDate(transactionDate) }}
            </div>
          </template>
          <template v-slot:cell(index)="row">{{ row.index + 1 }}.</template>
          <template v-slot:cell(adjustmentQty)="row">
            {{ formatAdjustQty(row.item.adjustmentQty) }}
          </template>
          <template v-slot:cell(totalQty)="row">
            {{ formatTotalQty(row.item.totalQty) }}
          </template>
        </b-table>
      </div>
    </div>

    <template v-slot:modal-footer>
      <div class="w-100 d-flex justify-content-end align-items-center">
        <b-button
          variant="outline-secondary"
          class="mr-2"
          @click="cancel"
        >
          ย้อนกลับ
        </b-button>
        <b-button
          variant="primary"
          @click="confirm"
        >
          ยืนยัน
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import moment from "moment";
import { formatDate } from "../../../utils/utils.js";

export default {
  name: "PhysicalCountPreviewModal",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    reason: {
      type: String,
      default: "",
    },
    transactionDate: {
      type: [Date, String],
      default: null,
    },
    countedDt: {
      type: [Date, String],
      default: null,
    },
    adjustmentNo: {
      type: String,
      default: "",
    },
    createdByName: {
      type: String,
      default: "",
    },
    createdDate: {
      type: [Date, String],
      default: null,
    },
  },
  computed: {
    fields() {
      return [
        { key: "index", label: "", thStyle: { width: "40px" } },
        { key: "productNo", label: "รหัส", thStyle: { minWidth: "100px" } },
        {
          key: "productName",
          label: "ชื่อสินค้า",
          thStyle: { minWidth: "160px" },
        },
        {
          key: "adjustmentQty",
          label: "จำนวนปรับปรุง",
          thStyle: { minWidth: "100px" },
          class: "text-right",
        },
        {
          key: "totalQty",
          label: "จำนวนสต๊อก",
          thStyle: { minWidth: "100px" },
          class: "text-right",
        },
        {
          key: "uomName",
          label: "หน่วยสินค้า",
          thStyle: { minWidth: "100px" },
        },
      ];
    },
  },
  methods: {
    show() {
      this.$refs.physicalCountPreviewModal.show();
    },
    hide() {
      this.$refs.physicalCountPreviewModal.hide();
    },
    cancel() {
      this.hide();
      this.$emit("cancel");
    },
    confirm() {
      this.hide();
      this.$emit("confirm");
    },
    onHidden() {
      this.$emit("hidden");
    },
    formatDate(value) {
      if (!value) return "-";
      return formatDate(value);
    },
    formatAdjustQty(value) {
      if (value === null || value === undefined || value === "") return "-";
      const num = Number(value);
      if (Number.isNaN(num)) return String(value);
      const formatted = num.toLocaleString("en-US");
      if (num > 0) return `+${formatted}`;
      return formatted;
    },
    formatTotalQty(value) {
      if (value === null || value === undefined || value === "") return "-";
      const num = Number(value);
      if (Number.isNaN(num)) return String(value);
      return num.toLocaleString("en-US");
    },
  },
};
</script>

<style scoped>
.preview-layout {
  display: flex;
  gap: 24px;
  border: 0px solid #dee2e6;
  padding: 20px;
}

.preview-info {
  flex: 0 0 30%;
  min-width: 0;
  border-right: 1px solid #dee2e6;
  padding-right: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.preview-table {
  flex: 1;
  min-width: 0;
  overflow-x: auto;
}

.info-row {
  line-height: 1.6;
  display: flex;
}

.info-label {
  font-weight: 600;
  color: #000;
  white-space: nowrap;
  flex-shrink: 0;
}

.info-value {
  margin-left: 4px;
  word-break: break-word;
  min-width: 0;
  flex: 1;
}

::v-deep #physicalCountPreviewTable thead th,
::v-deep #physicalCountPreviewTable tbody td {
  vertical-align: middle !important;
}

::v-deep #physicalCountPreviewTable tbody tr:last-child td {
  border-bottom: 1px solid #dee2e6 !important;
}

@media (max-width: 768px) {
  .preview-layout {
    flex-direction: column;
  }

  .preview-info {
    flex: none;
    border-right: none;
    padding-right: 0;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 16px;
  }
}
</style>
